import {
 Box,
 Button,
 Dialog,
 DialogActions,
 DialogContent,
 DialogTitle,
 useMediaQuery,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import themeOptions from '../../../utils/theme';
import CloseButton from '../CloseButton';
import XTabs from '../XTabs';
import PasswordTab from './PasswordTab';
import UserTab from './UserTab';
import { useUserProfileForm } from './hooks/useUserProfileForm';
import { UserProfileModalProps } from './types';

const UserProfileModal: React.FC<UserProfileModalProps> = ({
 open,
 onClose,
}) => {
 const { t } = useTranslation();
 const isLargerThanMd = useMediaQuery(themeOptions().breakpoints.up('md'));

 const {
  control,
  errors,
  watch,
  handleSubmit,
  handleFormSubmit,
  handleSaveClick,
  handleCancel,
  activeTab,
  setActiveTab,
  user,
  groups,
  loading,
  twoFactorEnabled,
  setTwoFactorEnabled,
  twoFactorAuthMethod,
  setTwoFactorAuthMethod,
  qrCode,
  setQrCode,
  verificationCode,
  setVerificationCode,
  handleGenerateQR,
  handle2FACodeSubmit,
  handleVerifyTotp,
  i18n,
 } = useUserProfileForm(open, onClose);

 const userTabContent = (
  <UserTab
   control={control}
   errors={errors}
   watch={watch}
   user={user}
   groups={groups}
   loading={loading}
   on2FACodeSubmit={handle2FACodeSubmit}
   i18n={i18n}
   twoFactorEnabled={twoFactorEnabled}
   twoFactorAuthMethod={twoFactorAuthMethod}
   setTwoFactorAuthMethod={setTwoFactorAuthMethod}
   qrCode={qrCode}
   verificationCode={verificationCode}
   onGenerateQR={handleGenerateQR}
   onVerifyTotp={handleVerifyTotp}
   onVerificationCodeChange={setVerificationCode}
   onTwoFactorChange={setTwoFactorEnabled}
   setQrCode={setQrCode}
   onSubmit={handleSubmit(handleFormSubmit)}
  />
 );

 const passwordTabContent = (
  <PasswordTab
   control={control}
   errors={errors}
   watch={watch}
   onSubmit={handleSubmit(handleFormSubmit)}
  />
 );

 const tabs = [
  { id: 'user', label: t('user.tabs.user'), content: userTabContent },
  {
   id: 'password',
   label: t('user.tabs.password'),
   content: passwordTabContent,
  },
 ];

 return (
  <Dialog
   open={open}
   onClose={onClose}
   fullWidth
   fullScreen={!isLargerThanMd}
   sx={{
    '& .MuiDialog-container': {
     '& .MuiPaper-root': { width: '100%', maxWidth: '400px' },
    },
   }}
  >
   <DialogTitle>{t('user.title')}</DialogTitle>

   <CloseButton closeDialog={onClose} />

   <DialogContent sx={{ py: 0, px: 0 }}>
    <XTabs
     tabs={tabs}
     defaultTab={0}
     variant="fullWidth"
     ariaLabel="user profile tabs"
     sx={{ px: 3 }}
     tabSx={{
      '& .Mui-selected': {
       color: (th) => th.palette.kemek.blue,
      },
      '& .MuiTabs-indicator': {
       backgroundColor: (th) => th.palette.kemek.blue,
      },
     }}
     panelSx={{ p: 0 }}
     onChange={(newValue) => setActiveTab(newValue)}
    />
   </DialogContent>

   <DialogActions sx={{ padding: '16px 24px', justifyContent: 'flex-end' }}>
    <Box sx={{ display: 'flex', gap: 1 }}>
     <Button type="button" onClick={handleCancel} size="large">
      {t('global.cancel')}
     </Button>
     <Button
      variant="contained"
      type="submit"
      form={activeTab === 0 ? 'user-profile-form' : 'password-change-form'}
      size="large"
      onClick={handleSaveClick}
      disabled={
       loading?.identityUpdateSelf === true || loading?.identityGetSelf === true
      }
     >
      {t('user.buttons.save')}
     </Button>
    </Box>
   </DialogActions>
  </Dialog>
 );
};

export default UserProfileModal;

